import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Send, Search, MoreVertical, 
  Archive, Trash2, Flag, UserX, ArrowLeft,
  Check, CheckCheck, Image as ImageIcon, Smile
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as messagingService from '../services/messagingService';
import { useSportsLang } from '../contexts/SportsLangContext';

const LABELS = {
  pt: {
    title: 'Mensagens',
    searchPlaceholder: 'Buscar conversas...',
    noConversations: 'Nenhuma conversa ainda',
    noConversationsHint: 'Comece uma conversa visitando perfis de atletas e clubes!',
    noMessages: 'Nenhuma mensagem ainda.',
    noMessagesHint: 'Envie a primeira mensagem!',
    welcomeTitle: 'Suas Mensagens',
    welcomeDesc: 'Envie mensagens privadas para atletas, clubes e profissionais do esporte.',
    messagePlaceholder: 'Escreva uma mensagem...',
    online: 'Online',
    noMsg: 'Sem mensagens',
    timeNow: 'Agora',
    unknown: 'Desconhecido',
  },
  en: {
    title: 'Messages',
    searchPlaceholder: 'Search conversations...',
    noConversations: 'No conversations yet',
    noConversationsHint: 'Start a conversation by visiting athlete and club profiles!',
    noMessages: 'No messages yet.',
    noMessagesHint: 'Send the first message!',
    welcomeTitle: 'Your Messages',
    welcomeDesc: 'Send private messages to athletes, clubs and sports professionals.',
    messagePlaceholder: 'Write a message...',
    online: 'Online',
    noMsg: 'No messages',
    timeNow: 'Now',
    unknown: 'Unknown',
  },
};

// === CORES & TEMA ===
const THEME = {
  primary: '#111111',
  gold: '#b48c2c',
  bg: '#f3f2ef',
  white: '#ffffff',
  text: '#1f2937',
  border: '#e5e7eb'
};

// === COMPONENTE 1: ITEM DA LISTA DE CONVERSAS ===
const ConversationItem = ({ conversation, isActive, onClick }) => {
  const { user } = useAuth();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  
  // Para DM 1:1, mostrar dados do outro usuário
  const displayName = conversation.type === 'direct' 
    ? conversation.other_user_name 
    : conversation.title;
  
  const displayAvatar = conversation.type === 'direct'
    ? conversation.other_user_avatar
    : null;
  
  const lastMessage = conversation.last_message_body || t.noMsg;
  const unreadCount = conversation.unread_count || 0;
  const isUnread = unreadCount > 0;
  
  // Formatação de tempo relativo
  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return t.timeNow;
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'pt-BR', { day: '2-digit', month: '2-digit' });
  };
  
  return (
    <div 
      onClick={onClick}
      className={`flex items-start gap-3 p-4 cursor-pointer transition-colors border-b border-gray-100 hover:bg-gray-50 ${
        isActive ? 'bg-green-50/30 border-l-4' : ''
      }`}
      style={isActive ? { borderLeftColor: THEME.primary } : {}}
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
          {displayAvatar ? (
            <img src={displayAvatar} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-lg font-bold text-gray-600">
              {displayName?.substring(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        {isUnread && (
          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: THEME.primary }}>
            <span className="text-[10px] font-bold text-white">{unreadCount}</span>
          </div>
        )}
      </div>
      
      {/* Conteúdo */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start mb-1">
          <h4 className={`text-sm truncate ${isUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
            {displayName || t.unknown}
          </h4>
          <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
            {formatTime(conversation.last_message_at)}
          </span>
        </div>
        <p className={`text-xs truncate ${isUnread ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>
          {lastMessage}
        </p>
      </div>
    </div>
  );
};

// === COMPONENTE 2: MENSAGEM NO CHAT ===
const MessageBubble = ({ message, isOwnMessage }) => {
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
        {/* Avatar (só para mensagens dos outros) */}
        {!isOwnMessage && (
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {message.sender_avatar ? (
                <img src={message.sender_avatar} alt={message.sender_name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs font-bold text-gray-600">
                  {message.sender_name?.substring(0, 2).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1 ml-1">{message.sender_name}</p>
              <div 
                className={`px-4 py-2.5 rounded-2xl ${isOwnMessage ? 'text-white rounded-br-sm' : 'bg-gray-100 text-gray-900 rounded-bl-sm'}`}
                style={isOwnMessage ? { backgroundColor: THEME.primary } : {}}
              >
                <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
              </div>
            </div>
          </div>
        )}
        
        {isOwnMessage && (
          <div>
            <div className="px-4 py-2.5 rounded-2xl text-white rounded-br-sm" style={{ backgroundColor: THEME.primary }}>
              <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
            </div>
          </div>
        )}
        
        {/* Timestamp e status */}
        <div className={`flex items-center gap-1 mt-1 px-2 ${isOwnMessage ? 'justify-end' : 'justify-start ml-10'}`}>
          <span className="text-xs text-gray-400">{formatTime(message.created_at)}</span>
          {isOwnMessage && (
            message.is_read ? (
              <CheckCheck size={14} style={{ color: THEME.gold }} />
            ) : (
              <Check size={14} className="text-gray-400" />
            )
          )}
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE 3: ÁREA DE CHAT (DIREITA) ===
const ChatArea = ({ conversation, userId, onBack, onSendMessage }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Buscar mensagens ao abrir conversa
  useEffect(() => {
    if (!conversation) return;
    
    const loadMessages = async () => {
      setLoading(true);
      try {
        const { messages: fetchedMessages } = await messagingService.getMessages(
          conversation.conversation_id,
          userId,
          { limit: 50 }
        );
        setMessages(fetchedMessages);
        
        // Marcar como lida
        if (fetchedMessages.length > 0) {
          const lastMessageId = fetchedMessages[fetchedMessages.length - 1].id;
          await messagingService.markAsRead(conversation.conversation_id, userId, lastMessageId);
        }
        
        // Scroll para o final
        setTimeout(() => scrollToBottom(), 100);
      } catch (error) {
        console.error('Erro ao carregar mensagens:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadMessages();
  }, [conversation, userId]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const sentMessage = await messagingService.sendMessage(
        conversation.conversation_id,
        userId,
        newMessage.trim()
      );
      
      // Adicionar mensagem à lista
      setMessages(prev => [...prev, sentMessage]);
      setNewMessage('');
      
      // Scroll para o final
      setTimeout(() => scrollToBottom(), 100);
      
      // Callback para atualizar a lista de conversas
      if (onSendMessage) onSendMessage();
      
      // Focar input novamente
      inputRef.current?.focus();
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setSending(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };
  
  if (!conversation) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-white p-8 text-center">
        <MessageSquare size={64} className="text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">{t.welcomeTitle}</h2>
        <p className="text-gray-500 max-w-md">
          {t.welcomeDesc}
        </p>
      </div>
    );
  }
  
  const otherUserName = conversation.other_user_name || conversation.title || 'Chat';
  
  return (
    <div className="flex-1 flex flex-col bg-white h-screen">
      {/* Header do Chat */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="md:hidden p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={20} />
          </button>
          
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
            {conversation.other_user_avatar ? (
              <img src={conversation.other_user_avatar} alt={otherUserName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-sm font-bold text-gray-600">
                {otherUserName?.substring(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          
          <div>
            <h3 className="font-bold" style={{ color: THEME.primary }}>{otherUserName}</h3>
            <p className="text-xs" style={{ color: THEME.gold }}>{t.online}</p>
          </div>
        </div>
        
        <button className="p-2 hover:bg-gray-100 rounded-full">
          <MoreVertical size={20} className="text-gray-600" />
        </button>
      </div>
      
      {/* Área de Mensagens */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50" style={{ maxHeight: 'calc(100vh - 140px)' }}>
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin w-8 h-8 border-4 border-gray-200 rounded-full" style={{ borderTopColor: THEME.primary }}></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare size={48} className="text-gray-300 mb-3" />
            <p className="text-gray-500">{t.noMessages}</p>
            <p className="text-sm text-gray-400">{t.noMessagesHint}</p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <MessageBubble 
                key={msg.id} 
                message={msg} 
                isOwnMessage={msg.sender_user_id === userId}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
      
      {/* Input de Mensagem */}
      <form onSubmit={handleSend} className="flex items-end gap-2 px-4 py-3 bg-white border-t border-gray-200">
        <button type="button" className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0">
          <ImageIcon size={20} className="text-gray-500" />
        </button>
        
        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t.messagePlaceholder}
            className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-full resize-none focus:outline-none focus:ring-2 focus:border-transparent text-sm max-h-24"
            rows={1}
            style={{ minHeight: '42px', '--tw-ring-color': THEME.primary }}
          />
          <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 hover:scale-110 transition-transform">
            <Smile size={20} className="text-gray-400" />
          </button>
        </div>
        
        <button 
          type="submit"
          disabled={!newMessage.trim() || sending}
          className="p-3 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 transition-all hover:shadow-lg"
          style={{ backgroundColor: THEME.primary }}
          onMouseEnter={(e) => !newMessage.trim() || sending ? null : e.target.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          {sending ? (
            <div className="animate-spin w-5 h-5 border-2 border-t-white border-gray-300 rounded-full"></div>
          ) : (
            <Send size={20} />
          )}
        </button>
      </form>
    </div>
  );
};

// === PÁGINA PRINCIPAL: CHAT ===
const ChatPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileChat, setShowMobileChat] = useState(false);
  
  // Buscar conversas ao carregar
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    loadConversations();
  }, [user, navigate]);
  
  const loadConversations = async () => {
    setLoading(true);
    try {
      const fetchedConversations = await messagingService.getConversations(user.id);
      setConversations(fetchedConversations);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setShowMobileChat(true);
  };
  
  const handleBackToList = () => {
    setShowMobileChat(false);
    setSelectedConversation(null);
  };
  
  const handleSendMessage = () => {
    // Recarregar conversas para atualizar última mensagem e timestamp
    loadConversations();
  };
  
  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const name = conv.type === 'direct' ? conv.other_user_name : conv.title;
    return name?.toLowerCase().includes(searchQuery.toLowerCase());
  });
  
  return (
    <div className="min-h-screen flex" style={{ backgroundColor: THEME.bg }}>
      {/* LISTA DE CONVERSAS (ESQUERDA) */}
      <div className={`${showMobileChat ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 bg-white border-r border-gray-200`}>
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200">
          <h1 className="text-xl font-bold mb-3" style={{ color: THEME.primary }}>{t.title}</h1>
          
          {/* Busca */}
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ '--tw-ring-color': THEME.primary }}
            />
          </div>
        </div>
        
        {/* Lista de Conversas */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin w-8 h-8 border-4 border-gray-200 rounded-full" style={{ borderTopColor: THEME.primary }}></div>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-4">
              <MessageSquare size={48} className="text-gray-300 mb-3" />
              <h3 className="font-bold text-gray-800 mb-1">{t.noConversations}</h3>
              <p className="text-sm text-gray-500">
                {t.noConversationsHint}
              </p>
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.conversation_id}
                conversation={conversation}
                isActive={selectedConversation?.conversation_id === conversation.conversation_id}
                onClick={() => handleSelectConversation(conversation)}
              />
            ))
          )}
        </div>
      </div>
      
      {/* ÁREA DE CHAT (DIREITA) */}
      <div className={`${showMobileChat ? 'flex' : 'hidden md:flex'} flex-1`}>
        <ChatArea 
          conversation={selectedConversation}
          userId={user?.id}
          onBack={handleBackToList}
          onSendMessage={handleSendMessage}
        />
      </div>
    </div>
  );
};

export default ChatPage;
